import { apiClient } from '~/api/client'

// 모델 레지스트리·프로파일 바인딩 API + 도메인 타입. 페이지/다이얼로그가 공유한다.
// (DESIGN.md §5.1 /admin/models)

export type ProviderType = 'managed' | 'self_hosted'
export type ModelKind = 'chat' | 'embedding' | 'vision'

export interface ModelRow {
    id: string
    name: string
    provider: string
    providerType: ProviderType
    modelId: string
    kind: ModelKind
    baseUrl: string | null
    active: boolean
    /** 키가 설정돼 있는지(암호화 키 또는 레거시 ref). 키 값/암호문은 내려오지 않음. */
    keySet: boolean
    /** 표시용 키 끝 4자리(비밀 아님). 없으면 null. */
    keyLast4: string | null
}

/** 단일 use-case 바인딩 행(읽기 뷰). */
export interface UseCaseBinding {
    useCase: string
    kind: ModelKind
    label: string
    /** 현재 바인딩된 모델 이름(없으면 용도 기본값/env로 폴백). */
    model: string | null
    /** 같은 용도(kind) 기본값에 바인딩된 모델(폴백 대상). */
    defaultModel: string | null
    /** 이 use-case가 용도 기본값(chat.default 등)인지. */
    isDefault: boolean
}

/** 용도(kind)별 use-case 바인딩 묶음. */
export interface UseCaseBindingGroup {
    kind: ModelKind
    defaultUseCase: string
    defaultModel: string | null
    bindings: UseCaseBinding[]
}

/** 모델 등록/편집 공통 본문. 빈 baseUrl/apiKey는 호출부에서 null로 정규화한다. */
export interface ModelInput {
    name: string
    provider: string
    providerType: ProviderType
    modelId: string
    kind: ModelKind
    baseUrl: string | null
    /** 실제 키(write-only). 비우면(null) 기존 키를 유지하고, 값이 있으면 서버가 암호화 저장한다. */
    apiKey: string | null
}

/** POST /models/test-connection 본문. 저장 전 원본 키로 연결을 점검한다(저장하지 않음). */
export interface TestConnectionInput {
    provider: string
    modelId: string
    kind: ModelKind
    baseUrl: string | null
    apiKey: string | null
}

/** POST /models/{id}/test 응답. ok=연결 성공, latencyMs=왕복 지연(ms), error=실패 사유. */
export interface TestModelResult {
    ok: boolean
    latencyMs: number
    model: string
    error?: string
}

// 색만으로 정보를 전달하지 않도록 라벨 텍스트를 함께 둔다(표·셀렉트 공용).
export const PROVIDER_TYPE_LABEL: Record<ProviderType, string> = {
    managed: '외부 관리형',
    self_hosted: '내부 자가호스팅',
}

export const KIND_LABEL: Record<ModelKind, string> = {
    chat: '채팅',
    embedding: '임베딩',
    vision: '비전',
}

export const listModels = () => apiClient.get<ModelRow[]>('/models')

export const listBindings = () =>
    apiClient.get<UseCaseBindingGroup[]>('/models/bindings')

export const createModel = (body: ModelInput) =>
    apiClient.post<ModelRow>('/models', body)

/** PUT /models/{id} — 편집(ModelInput 일부) 또는 활성 토글({active}). */
export const updateModel = (
    id: string,
    body: Partial<ModelInput> & { active?: boolean }
) => apiClient.put<ModelRow>(`/models/${id}`, body)

export const deleteModel = (id: string) => apiClient.delete(`/models/${id}`)

/** POST /models/{id}/test — 저장된 키(복호화)로 모델에 핑을 보내 연결을 확인한다. */
export const testModel = (id: string) =>
    apiClient.post<TestModelResult>(`/models/${id}/test`)

/** POST /models/test-connection — 저장 전에 원본 키로 연결을 점검한다(키는 저장하지 않음). */
export const testConnection = (body: TestConnectionInput) =>
    apiClient.post<TestModelResult>('/models/test-connection', body)

/** PUT /models/bindings/{useCase} — use-case에 모델을 바인딩(용도 일치 필요). */
export const bindUseCase = (useCase: string, model: string) =>
    apiClient.put<UseCaseBinding>(
        `/models/bindings/${encodeURIComponent(useCase)}`,
        { model }
    )

/** DELETE /models/bindings/{useCase} — 바인딩 해제(용도 기본값/env로 폴백, superadmin). */
export const unbindUseCase = (useCase: string) =>
    apiClient.delete(`/models/bindings/${encodeURIComponent(useCase)}`)
